import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Table, Button, Space, Tag, Popconfirm, Tooltip, Switch } from "antd";
import { Pencil, Trash2, Plus } from "lucide-react";
import { IVariant } from "../../../types/api/product";
import { useVariantList, useVariantDelete, useVariantUpdate } from "../../../hooks/api/variant/hooks";
import VariantModal from "./VariantModal";

interface VariantListProps {
  productId: string;
}

const VariantList: React.FC<VariantListProps> = ({ productId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<IVariant | null>(null);
  
  const [qData, setQData] = useState({
    productId,
    page: 1,
    limit: 10,
  });

  const { data, isLoading } = useVariantList(qData, { enabled: !!productId });
  const queryClient = useQueryClient();
  const deleteMutation = useVariantDelete({ qData });
  const updateMutation = useVariantUpdate({ 
    qData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variants"] });
    }
  });
  
  const handleToggleDefault = (record: IVariant, checked: boolean) => {
    updateMutation.mutate({ id: record._id, data: { isDefault: checked } as any });
  };

  const handleAdd = () => {
    setSelectedVariant(null);
    setModalOpen(true);
  };

  const handleEdit = (record: IVariant) => {
    setSelectedVariant(record);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "images",
      key: "images",
      render: (images: string[]) =>
        images?.[0] ? (
          <img
            src={images[0]}
            alt="Variant"
            className="w-10 h-10 object-cover rounded border border-slate-200"
          />
        ) : (
          <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400 text-[9px]">
            No Image
          </div>
        ),
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      render: (text: string, record: IVariant) => (
        <span>
          {text}
        </span>
      )
    },
    {
      title: "Default",
      dataIndex: "isDefault",
      key: "isDefault",
      render: (isDefault: boolean, record: IVariant) => (
        <Switch
          checked={isDefault}
          onChange={(checked) => handleToggleDefault(record, checked)}
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number, record: IVariant) =>
        record.isPriceOnApplication ? (
          <Tag color="orange">POA</Tag>
        ) : (
          `${record.currency} ${price}`
        ),
    },
    {
      title: "Editions Available",
      dataIndex: "stock",
      key: "stock",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>{status ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_: any, record: IVariant) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => handleEdit(record)}
              className="text-slate-500 hover:text-blue-600 border-none shadow-none bg-transparent"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this variant?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record._id)}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
            >
              <Button
                size="small"
                icon={<Trash2 size={14} />}
                className="text-slate-500 hover:text-red-600 border-none shadow-none bg-transparent"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        The photos, price, size, material, and sale details for this
        sculpture&apos;s first (default) edition are set on the{" "}
        <span className="font-medium">Basic Info</span> tab. Use this tab
        to add further sizes or editions, or to set Finish/Method/Base.
      </div>
      <div className="flex justify-end">
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={handleAdd}
          className="flex items-center gap-2"
        >
          Add Edition
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data?.data || []}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          current: qData.page,
          pageSize: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (page, pageSize) => {
            setQData((prev) => ({ ...prev, page, limit: pageSize }));
          },
        }}
        size="small"
      />

      <VariantModal
        productId={productId}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedItem={selectedVariant}
        qData={qData}
      />
    </div>
  );
};

export default VariantList;
